// ─── What: pan/zoom viewport state ────────────────────────────────────────────
//
// The chart has two notions of "what window am I looking at". `timeRange` is the
// committed one — the parent owns it and refetches on it. `liveDomain` is the
// local override that wheel-zoom and drag-pan write on every frame, so the
// already-rendered data rescales instantly while the refetch is debounced.
//
// Keeping them apart is what stops a drag from hammering the API, and keeping
// them HERE is what stops that concern from living inside the render effect.

import { useCallback, useEffect, useRef, useState } from 'react';

const COMMIT_DEBOUNCE_MS = 300;

export interface ChartViewport {
    liveDomain: [Date, Date] | null;
    setLiveDomain: (d: [Date, Date] | null) => void;
    /** Debounced hand-off to the parent. Cancels any pending commit. */
    commitRange: (range: [Date, Date] | null) => void;
    /** Detaches an in-progress drag's window listeners (set while dragging). */
    dragCleanupRef: React.MutableRefObject<(() => void) | null>;
    /** Which time bound a drag is currently pushing against, if any. */
    edgeHitRef: React.MutableRefObject<'start' | 'end' | null>;
}

export function useChartViewport(
    timeRange: [Date, Date] | null,
    onTimeRangeChange: (range: [Date, Date] | null) => void
): ChartViewport {
    const [liveDomain, setLiveDomain] = useState<[Date, Date] | null>(null);
    const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragCleanupRef = useRef<(() => void) | null>(null);
    const edgeHitRef = useRef<'start' | 'end' | null>(null);

    const commitRange = useCallback(
        (range: [Date, Date] | null) => {
            if (commitTimer.current) clearTimeout(commitTimer.current);
            commitTimer.current = setTimeout(
                () => onTimeRangeChange(range),
                COMMIT_DEBOUNCE_MS
            );
        },
        [onTimeRangeChange]
    );

    // Follow the prop once a committed range lands; clears the live override.
    useEffect(() => {
        setLiveDomain(null);
    }, [timeRange]);

    // An unmount mid-drag would otherwise leave window listeners attached.
    useEffect(
        () => () => {
            if (commitTimer.current) clearTimeout(commitTimer.current);
            dragCleanupRef.current?.();
        },
        []
    );

    return {
        liveDomain,
        setLiveDomain,
        commitRange,
        dragCleanupRef,
        edgeHitRef,
    };
}
